﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as api from "~base/Services/API";
import type { ApiHookFuncReturnProps } from "~base/Services/API";
import * as sessionModel from "~base/Services/API/Steps/Model";
import type {
    GuideDataStepSpec,
    GuideDataViewSpec,
    MetaSpecModel,
    ProcessSpecModel,
} from "~base/Services/API/Steps/Model";
import type { DispResBase } from "~base/Components/Guide/GuideStepTypes";
import type { RecordedApiCall } from "~base/Services/MockContext";

//-------------------------------------------------------------------------------------
// Http Contract
//-------------------------------------------------------------------------------------

const uri = (
    extSessionId: string,
    sessionModelFqn: string,
    locale: string,
    subjectIdentifier: string,
    subjectIdentifierType: string
): string => {
    let uri = `Api/SoPInvite/v1/Accept?extSessionId=${encodeURIComponent(extSessionId)}&sessionModelFqn=${encodeURIComponent(sessionModelFqn)}&locale=${encodeURIComponent(locale)}`;

    if (subjectIdentifier) {
        uri += `&subjectIdentifier=${encodeURIComponent(subjectIdentifier)}`;
    }

    if (subjectIdentifierType) {
        uri += `&subjectIdentifierType=${encodeURIComponent(subjectIdentifierType)}`;
    }

    return uri;
};
const method = "POST";

//-------------------------------------------------------------------------------------
// Types
//-------------------------------------------------------------------------------------

export interface PostAcceptInviteRequest {}

export interface PostAcceptInviteResponse {
    sessionId: number;
}

export interface PostAcceptInviteProps extends api.ApiHookBaseProps<
    PostAcceptInviteRequest,
    PostAcceptInviteResponse,
    unknown
> {
    extSessionId: string;
    sessionModelFqn: string;
    locale: string;
    subjectIdentifier?: string;
    subjectIdentifierType?: string;
}

//-------------------------------------------------------------------------------------
// API Hook
//-------------------------------------------------------------------------------------

export interface PostAcceptInviteFuncResponse extends ApiHookFuncReturnProps<
    PostAcceptInviteRequest,
    PostAcceptInviteResponse,
    unknown
> {}

export function usePostAcceptInvite(
    props: PostAcceptInviteProps
): [
    boolean,
    (data: PostAcceptInviteProps) => Promise<PostAcceptInviteFuncResponse>,
] {
    const apiParams: api.ApiHookProps<
        PostAcceptInviteRequest,
        PostAcceptInviteResponse,
        unknown
    > = {
        ...props,
        uri: uri(
            props.extSessionId,
            props.sessionModelFqn,
            props.locale,
            props.subjectIdentifier,
            props.subjectIdentifierType
        ),
        method: method,
    };

    const [loading, invoker] = api.useApi<
        PostAcceptInviteRequest,
        PostAcceptInviteResponse,
        unknown
    >(apiParams);
    return [
        loading,
        (data: PostAcceptInviteProps) =>
            invoker(
                uri(
                    props.extSessionId,
                    props.sessionModelFqn,
                    props.locale,
                    props.subjectIdentifier,
                    props.subjectIdentifierType
                ),
                null
            ),
    ];
}
